import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { FormattedMessage, FormattedList } from 'LearningCenterUI/components/I18n';
import UIList from 'UIComponents/list/UIList';
import UICardGrid from 'UIComponents/card/UICardGrid';
import H4 from 'UIComponents/elements/headings/H4';
import H7 from 'UIComponents/elements/headings/H7';
import getLang from 'I18n/utils/getLang';
import { useCallback, useMemo } from 'react'; // @ts-expect-error Could not find a declaration file for module

import ContentOverview from './ContentOverview';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIBox from 'UIComponents/layout/UIBox';
import { SkeletonBlock, SkeletonWrapper } from '../Skeleton';
import { BATTLESHIP, EERIE } from 'HubStyleTokens/colors'; // @ts-expect-error Could not find a declaration file for module

import useMediaQuery from '../../hooks/useMediaQuery';
import { device } from '../../constants/StyledComponentConstants';
import styled from '../../utils/Styling';
import UsageTracker from '../../utils/UsageTracker';
import { useFullTrack } from '../../features/tracks';
import { RECOMMENDED_FOR } from '../../features/certificates/certificatesConstants';
import { useAssetStatus } from '../../features/userProgress';
import TrackThumbnailOverlayed from '../TrackCard/Thumbnail/TrackThumbnailOverlayed';
const TRANSLATED_IDS = [23, 24, 29, 1028124, 15, 117, 11, 25, 9, 5, 12, 116, 36, 66, 19, 97, 87];
const FlushList = styled(UIList).withConfig({
  displayName: "ExpandedCertificate__FlushList",
  componentId: "ag5juc-0"
})(["padding-inline-start:16px;margin-bottom:8px;"]);
const HoverableCardGrid = styled((_ref) => {
  let props = Object.assign({}, _ref);
  const isMobile = useMediaQuery(`screen and ${device.mobile}`);
  return /*#__PURE__*/_jsx(UICardGrid, Object.assign({}, props, {
    gutterSize: isMobile ? 20 : undefined
  }));
}).withConfig({
  displayName: "ExpandedCertificate__HoverableCardGrid",
  componentId: "ag5juc-1"
})(["padding:8px;margin:-8px;border-radius:5px;transition:background-color 200ms;margin-top:16px;&:hover{background-color:", "19;cursor:pointer;}"], EERIE);
const Heading = styled(H4).withConfig({
  displayName: "ExpandedCertificate__Heading",
  componentId: "ag5juc-2"
})(["font-weight:500;"]);
const SubHeading = styled(H7).attrs(() => ({
  'aria-level': 5
})).withConfig({
  displayName: "ExpandedCertificate__SubHeading",
  componentId: "ag5juc-3"
})(["font-weight:500;"]);

const ExpandedCertificate = ({
  trackId,
  position
}) => {
  const isLaptop = useMediaQuery(`screen and ${device.laptop}`);
  const isMobile = useMediaQuery(`screen and ${device.mobile}`);
  const {
    status
  } = useAssetStatus({
    id: trackId,
    type: 'TRACK'
  });
  const inProgress = status === 'IN_PROGRESS';
  const {
    track,
    trackLoading: loading
  } = useFullTrack(trackId, getLang().toUpperCase());
  const trackAggregatedLessonContents = useMemo(() => {
    return track && track.lessons.reduce((aggregated, lesson) => [...aggregated, ...lesson.content], []);
  }, [track]);
  const handleClick = useCallback(() => {
    UsageTracker.track('interaction', {
      action: 'content-card-click',
      contentType: 'TRACK',
      trackId,
      appearsIn: 'popular-certificates',
      position,
      name: track && track.name,
      style: 'expanded',
      isInProgress: inProgress,
      isCertification: true
    });
  }, [trackId, position, inProgress, track]);

  if (!trackId || !track) {
    return null;
  }

  return /*#__PURE__*/_jsxs(HoverableCardGrid, {
    columns: isLaptop ? 1 : 2,
    "data-test-id": "expanded-certificate",
    onClick: handleClick,
    children: [/*#__PURE__*/_jsx(TrackThumbnailOverlayed, {
      trackId: trackId
    }), /*#__PURE__*/_jsxs(UIFlex, {
      direction: "column",
      children: [/*#__PURE__*/_jsx(Heading, {
        children: track.name
      }), track.description, !isMobile && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(SubHeading, {
          className: "m-bottom-1 m-top-3",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.mostPopularCertificates.recommendedFor"
          })
        }), /*#__PURE__*/_jsx(FlushList, {
          styled: true,
          children: RECOMMENDED_FOR[trackId] && /*#__PURE__*/_jsx(FormattedList, {
            children: RECOMMENDED_FOR[trackId].map(persona => /*#__PURE__*/_jsx(FormattedMessage, {
              message: `learningCenter.mostPopularCertificates.recommendedForRoles.${persona}`
            }, `${trackId}.${persona}`))
          })
        }), /*#__PURE__*/_jsx(SubHeading, {
          className: "m-bottom-1 m-top-3",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.mostPopularCertificates.whatYouLearn"
          })
        }), TRANSLATED_IDS.includes(trackId) && /*#__PURE__*/_jsxs(FlushList, {
          styled: true,
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: `learningCenter.mostPopularCertificates.whatYouLearnBulletPoints.${trackId}.1`
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: `learningCenter.mostPopularCertificates.whatYouLearnBulletPoints.${trackId}.2`
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: `learningCenter.mostPopularCertificates.whatYouLearnBulletPoints.${trackId}.3`
          })]
        }), /*#__PURE__*/_jsx(UIBox, {
          grow: 1,
          style: {
            width: '100%'
          },
          children: /*#__PURE__*/_jsx(UIFlex, {
            align: "end",
            style: {
              height: '100%',
              width: '100%'
            },
            children: /*#__PURE__*/_jsxs("div", {
              style: {
                width: '100%'
              },
              children: [/*#__PURE__*/_jsx(SubHeading, {
                className: "m-top-3",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "learningCenter.mostPopularCertificates.includes"
                })
              }), loading || !trackAggregatedLessonContents ? /*#__PURE__*/_jsx(SkeletonWrapper, {
                style: {
                  width: '60%'
                },
                children: /*#__PURE__*/_jsx(SkeletonBlock, {
                  color: BATTLESHIP,
                  style: {
                    height: 24,
                    width: '100%'
                  }
                })
              }) : /*#__PURE__*/_jsx(ContentOverview, {
                content: trackAggregatedLessonContents,
                hasCertificate: true,
                hasStudyGuide: track && !!track.studyGuide
              })]
            })
          })
        })]
      })]
    })]
  });
};

export default ExpandedCertificate;